import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LoadingButton from "../../common/LoadingButton";
import { Alert, Button, Form, FormCheck } from "react-bootstrap";
import MEMBER_TIMERANGE_MUTATION from "../queries/MEMBER_TIMERANGE_MUTATION";
import { ApolloError, useMutation } from "@apollo/client";
import { isFutureDate, removeTypename } from "../../utils";
import AlertDismissible from "../../common/AlertDismissible";
import MemberRoleItem from "./MemberRoleItem";
import { MemberRole } from "../types/MemberRole";
import MEMBERS_QUERY from "../queries/MEMBERS_QUERY";
import { AvailableRole } from "../types/AvailableRole";
import { isFuture } from "date-fns";
export const isNew = (role: MemberRole | undefined) => !role || !role?.GroupMemberRoleId;

type ModalProps = {
  changed: any
  modalMode: string | null
  member: any
  editMemberError: ApolloError | undefined
  isActive: boolean
  roles: AvailableRole[]
  saving: boolean
  setChanged: (c: any) => void;
  setActive: () => void;
  setInactive: () => void;
  setModalMode: (c: string | null) => void;
};

const getGraphQLErrorMessage = (error: ApolloError) => {
  return error.message;
};

const RoleModal = ({ changed, modalMode, member, editMemberError, isActive, roles, saving, setChanged, setInactive, setActive, setModalMode }: ModalProps) => {
  console.log('rendering RoleModal in mode: ', modalMode);
  const seen: any = {};
  if (changed?.Roles && changed.Roles.length) {
    console.log('Has roles');
    changed.Roles.forEach((r: MemberRole) => {
      if (seen[r.Role]) {
        if (!r.GroupMemberRoleId) {
          seen["new" + r.Role] = r;
        } else {
          const endDate = r.RoleEndDate ? new Date(r.RoleEndDate) : new Date();
          console.log("seen", seen[r.Role].RoleEndDate, endDate);
          const existingEndDate = new Date(seen[r.Role].RoleEndDate);
          if (endDate.getTime() > existingEndDate.getTime()) {
            console.log("replacing", r);
            seen[r.Role] = r;
          }
        }
      } else {
        seen[r.Role] = r;
      }
    });
  }
  console.log(seen, changed?.Roles);
  const displayRoles = Object.keys(seen)
    .map((k) => seen[k] as MemberRole)
    .sort((a: MemberRole, b: MemberRole) => {
      const endDate = b.RoleEndDate ? new Date(b.RoleEndDate) : new Date();
      const otherEndDate = a.RoleEndDate ? new Date(a.RoleEndDate) : new Date();
      return otherEndDate.getTime() - endDate.getTime();
    });
  if (modalMode) {
    return (
      <tr key={member.LookupId + "edit"}>
        <td colSpan={1000} className="p-0">
          <div className="p-4 bg-white">
            {editMemberError && (
              <AlertDismissible variant="warning">{getGraphQLErrorMessage(editMemberError || "There was a problem saving your changes.")}</AlertDismissible>
            )}
            {modalMode === "editedSuccess" && (
              <>
                <Alert variant="success">You updated {member.Name}.</Alert>
              </>
            )}
            {!isActive && modalMode === "editing" && <Alert variant="danger">Editing of roles is disabled on inactive volunteers.</Alert>}
            {(modalMode === "editing" || modalMode === "editedSuccess") && (
              <>
                {member.PrimaryContact === "Yes" && <Alert variant="danger">This record cannot be edited.</Alert>}
                {displayRoles.map((r: MemberRole, i: number) => {
                  return (
                    <MemberRoleItem
                      enabled={member.PrimaryContact !== "Yes" && isActive}
                      role={r}
                      roles={roles}
                      key={r.GroupMemberRoleId || `new-${i}`}
                      memberEnd={changed.EndDate}
                      memberStart={changed.StartDate}
                      addRole={() =>
                        setChanged({
                          ...changed,
                          Roles: [
                            ...changed.Roles,
                            {
                              Role: "",
                              RoleStartDate: new Date().toLocaleDateString(),
                            },
                          ],
                        })
                      }
                      removeRole={() =>
                        setChanged({
                          ...changed,
                          Roles: [...changed.Roles.slice(0, -1)],
                        })
                      }
                      saving={saving}
                      save={(editedObj: MemberRole) => {
                        const hasNew = changed.Roles.some((r: MemberRole) => isNew(r));
                        const changedRoles = [
                          ...changed.Roles.map((cr: MemberRole) => {
                            let roleItem;
                            if (cr.GroupMemberRoleId === editedObj?.GroupMemberRoleId) {
                              roleItem = editedObj;
                            } else {
                              roleItem = cr;
                            }
                            if (hasNew && !isNew(roleItem) && (!roleItem.RoleEndDate || isFuture(new Date(roleItem.RoleEndDate)))) {
                              var yesterday = new Date();
                              yesterday.setDate(yesterday.getDate() - 1);
                              roleItem = {
                                ...roleItem,
                                RoleEndDate: yesterday.toUTCString(),
                              };
                            }
                            return roleItem;
                          }),
                        ];
                        const newObj = {
                          ...changed,
                          _pending: true,
                          Roles: [...changedRoles],
                        };
                        setChanged(newObj);
                      }}
                      isLast={displayRoles.length === ++i}
                    />
                  );
                })}
              </>
            )}
            {modalMode === "confirmActiveChange" && (
              <>
                You are setting <strong>{member.Name}</strong> to <strong>{isActive ? "Inactive" : "Active"}</strong>. Do you want to proceed?
                <LoadingButton
                  loading={saving}
                  variant={isActive ? "danger" : "success"}
                  className="mx-3"
                  size="sm"
                  onClick={() => (isActive ? setInactive() : setActive())}
                >
                  Yes
                </LoadingButton>
                <Button size="sm" variant="secondary" onClick={() => setModalMode(null)}>
                  No
                </Button>
              </>
            )}
          </div>
        </td>
      </tr>
    );
  }
  return null;
};

const MemberListItem = ({ member, roles }: { member: any; roles: AvailableRole[] }) => {
  const [modalMode, setModalMode] = useState<string | null>(null);
  const [changed, setChanged] = useState<any>();
  const defaultRole = useRef<MemberRole>({
    Role: "Chapter Committee Member",
    RoleStartDate: new Date().toLocaleDateString(),
    RoleEndDate: null,
  });
  const isActive = !member.EndDate;
  const currentRoles = changed?.Roles?.filter((r: any) => !r.RoleEndDate || isFutureDate(r.RoleEndDate));
  const setActive = () => {
    const pending = {
      ...changed,
      EndDate: null,
      Roles: [
        ...changed.Roles,
        defaultRole.current,
      ],
      _pending: true,
    };
    setChanged(pending);
  };
  const setActiveCallback = useCallback(setActive, [changed]);

  const setInactive = () => {
    const pending = {
      ...changed,
      EndDate: new Date().toUTCString(),
      Roles: [
        ...changed.Roles.map((r: MemberRole) => {
          if (!r.RoleEndDate) {
            return {
              ...r,
              RoleEndDate: new Date(new Date().setDate(new Date().getDate() - 1)).toUTCString(),
            };
          }
          return r;
        }),
      ],
      _pending: true,
    };
    setChanged(pending);
  };
  const setInactiveCallback = useCallback(setInactive, [changed]);

  const getEditedData = () => {
    const edited = {
      ...member,
      ...changed,
    };
    delete edited._pending;
    return removeTypename(edited);
  };

  const [editMember, { loading: saving, error: editMemberError }] = useMutation(MEMBER_TIMERANGE_MUTATION, {
    variables: {
      volunteerData: {
        ...getEditedData(),
      },
    },
    onCompleted(data: any) {
      console.log(data);
      setModalMode("editedSuccess");
    },
    refetchQueries: [MEMBERS_QUERY, "Members"],
  });

  useEffect(() => {
    console.log(defaultRole);
    let memberRoles: MemberRole[];
    
    if (member?.Roles && !member?.Roles.length) {
      memberRoles = [{ ...defaultRole.current, Role: "" }];
    } else {
      memberRoles = [...member.Roles];
    }
    if (memberRoles && memberRoles?.length) {
      setChanged({ ...member, Roles: [...memberRoles] });
    }
  },[member, defaultRole]);

  useEffect(() => {
    if (changed?._pending && !saving) {
      console.log('pending changes, saving...')
      editMember();
      setChanged({ ...changed, _pending: false });
    }
  }, [changed, saving, editMember]);

  return (
    <React.Fragment key={member.LookupId}>
      <tr className={member.PrimaryContact === "Yes" ? "fw-bold" : ""}>
        <td className="align-middle">
          {member.Name}{" "}
          {member.StartDate ? (
            <small>
              ({member.StartDate} - {member.EndDate || "Present"}){" "}
            </small>
          ) : null}
        </td>
        <td className="text-center align-middle">
          <Form>
            <FormCheck
              disabled={member.PrimaryContact === "Yes"}
              type="switch"
              id={`active-${member.LookupId}`}
              checked={modalMode === "confirmActiveChange" ? !isActive : isActive}
              label=""
              aria-label="is published"
              onChange={() => {
                setModalMode(modalMode === "confirmActiveChange" ? null : "confirmActiveChange");
              }}
            />
          </Form>
        </td>
        <td className="align-middle">
          {currentRoles?.filter((r: MemberRole) => !!r.Role).map((r: MemberRole, i: number) => (
            <span key={r.GroupMemberRoleId || `new-${i}`}>
              {r.Role}{" "}
              <small>
                ({r.RoleStartDate} - {r.RoleEndDate || "Present"}){" "}
              </small>
            </span>
          ))}
        </td>
        <td>
          <span
            className="px-2 py-1 d-block text-end"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setModalMode(modalMode ? null : "editing");
            }}
          >
            <FontAwesomeIcon color="#868e96" icon={modalMode ? "caret-up" : "caret-down"} />
          </span>
        </td>
      </tr>
      <RoleModal
        changed={changed}
        modalMode={modalMode}
        member={member}
        editMemberError={editMemberError}
        isActive={isActive}
        roles={roles}
        saving={saving}
        setChanged={setChanged}
        setInactive={setInactiveCallback}
        setActive={setActiveCallback}
        setModalMode={setModalMode}
      />
    </React.Fragment>
  );
};

export default MemberListItem;
